import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Music2, Send } from "lucide-react";
import { absUrl } from "./media";
import { renderInline } from "./renderInline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated artists — a short strip of players + links to each artist's page.
export const FeaturedArtistsBlock = ({ block }) => {
  const cfg = block.config || {};
  const artists = (cfg.artists || []).filter((a) => a && a.name);
  return (
    <section className="rounded-2xl bg-[#0f1115] text-white px-5 sm:px-8 py-7" data-testid="featured-artists">
      <div className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.25em] text-[#F2CC8F]"><Music2 className="h-4 w-4" /> {cfg.eyebrow || "Listen while you read · Artists from our community"}</div>
      {cfg.title && <h2 className="mt-2 font-['Playfair_Display'] text-2xl md:text-3xl font-bold">{cfg.title}</h2>}
      {cfg.body && <p className="mt-2 text-white/75 max-w-3xl">{renderInline(cfg.body, "#F2CC8F")}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {artists.map((a, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid="featured-artist">
            <div className="flex items-center gap-3">
              {a.image && <img src={absUrl(a.image)} alt={a.name} className="h-14 w-14 rounded-full object-cover" loading="lazy" />}
              <div className="min-w-0">
                <div className="font-bold leading-tight">{a.name}</div>
                {a.tagline && <div className="text-xs text-white/60 leading-snug">{a.tagline}</div>}
              </div>
            </div>
            {a.audio_url && <audio src={absUrl(a.audio_url)} controls preload="none" className="w-full mt-3 h-9" />}
            {a.embed_url && !a.audio_url && <iframe src={a.embed_url} title={a.name} className="w-full h-[120px] mt-3 border-0 rounded-lg" allow="autoplay" />}
            {a.link && <Link to={a.link} className="mt-3 inline-block text-sm font-bold text-[#F2CC8F] underline underline-offset-2">{a.link_label || `More from ${a.name} →`}</Link>}
          </div>
        ))}
      </div>
    </section>
  );
};

// "Suggest changes" — stored privately for admin review; nothing publishes automatically.
export const SuggestChangesBlock = ({ block, layoutKey }) => {
  const cfg = block.config || {};
  const [form, setForm] = useState({ name: "", role: "", email: "", message: "" });
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await axios.post(`${API}/onscreen/suggest`, { ...form, page_key: layoutKey || cfg.page_key || "onscreen" });
      setState("done");
    } catch { setState("error"); }
  };
  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <section className="rounded-2xl border border-[#E2E0DC] bg-white px-5 sm:px-8 py-7" data-testid="suggest-changes">
      <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#1A1A1A]">{cfg.title || "Suggest changes to this page"}</h2>
      <p className="mt-2 text-[#5C5C5C] max-w-3xl">{cfg.body || "Corrections, additions, photos, credits — send them here. Nothing you submit is published automatically; our editor reviews every note before anything changes."}</p>
      {state === "done" ? (
        <div className="mt-5 rounded-xl bg-[#EEF3FF] border border-[#1D4ED8]/30 px-5 py-4 text-[#1D4ED8] font-semibold" data-testid="suggest-done">Thank you — received. We'll confirm with you before anything goes live.</div>
      ) : (
        <form onSubmit={submit} className="mt-5 grid gap-3 sm:grid-cols-2">
          <input required placeholder="Your name" className="rounded-lg border border-[#E2E0DC] px-3 py-2.5" {...f("name")} data-testid="suggest-name" />
          <input placeholder="Role on the project (optional)" className="rounded-lg border border-[#E2E0DC] px-3 py-2.5" {...f("role")} data-testid="suggest-role" />
          <input type="email" placeholder="Email (so we can confirm)" className="rounded-lg border border-[#E2E0DC] px-3 py-2.5 sm:col-span-2" {...f("email")} data-testid="suggest-email" />
          <textarea required rows={5} placeholder="What should change, and where?" className="rounded-lg border border-[#E2E0DC] px-3 py-2.5 sm:col-span-2" {...f("message")} data-testid="suggest-message" />
          <div className="sm:col-span-2 flex items-center gap-3">
            <button type="submit" disabled={state === "sending"} className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] text-white px-6 py-3 font-extrabold hover:bg-[#1e40af] disabled:opacity-60" data-testid="suggest-submit"><Send className="h-4 w-4" /> {state === "sending" ? "Sending…" : "Send for review"}</button>
            {state === "error" && <span className="text-sm text-red-600">Something went wrong — please try again.</span>}
          </div>
        </form>
      )}
    </section>
  );
};
